import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataProvider } from "react-admin";
import { ResourceEditor } from "./ResourceEditor/ResourceEditor";
import { SelectCreateScreen } from "./ResourceEditor/SelectCreateScreen";

type AmenitiesManagerProps = {
  id: string;
};

export const AmenitiesManager = ({ id }: AmenitiesManagerProps) => {
  const dataProvider = useDataProvider();
  const [enabledResources, setEnabledResources] = useState([]);

  const fetchPropertyResources = useCallback(async () => {
    const { data } = await dataProvider.getPropertyAmenities({ id: id });
    return data;
  }, [dataProvider, id]);

  const fetchEnabledResources = useCallback(async () => {
    const { data } = await dataProvider.getEnabledAmenities();
    setEnabledResources(data);
  }, [dataProvider]);

  const updateResources = useCallback(
    async (resources: any[]) =>
      dataProvider.updatePropertyAmenities(id, {
        amenities: resources.map((a: any) => a.id),
      }),
    [dataProvider, id],
  );

  const handleMoveItems = useCallback(
    (resources: any[]) => updateResources(resources),
    [updateResources],
  );

  const handleRemoveItem = useCallback(
    (resources: any[], removedResource: any) =>
      updateResources(
        resources.filter((item: any) => item.id !== removedResource.id),
      ),
    [updateResources],
  );

  const CreateScreen = useMemo(
    () => (props: any) => (
      <SelectCreateScreen
        {...props}
        label="Amenity"
        onSave={(resources, selectedResource) =>
          updateResources([...resources, selectedResource])
        }
        getSelectOptions={(resources) =>
          enabledResources.filter(
            (type: any) => !resources.some((res) => res.id === type.id),
          )
        }
      />
    ),
    [enabledResources, updateResources],
  );

  useEffect(() => {
    fetchEnabledResources();
  }, []);

  const RenderName = (resource: any) => {
    return (
      <div className="resource-with-icon">
        {resource.iconUpload && <img src={resource.iconUpload.uri} alt="amenity icon" width={20} height={20} />}
        <span>{resource.name}</span>
      </div>
    )
  }

  return (
    <ResourceEditor
      hasOrder
      hasBorder
      renderName={RenderName}
      resourceId="amenities"
      heading="Amenities"
      createNewLabel="Add New Amenity"
      updatingOrderMsg="Updating amenities order..."
      updatedOrderMsg="Amenities order updated."
      fetchResourceItems={fetchPropertyResources}
      CreateScreen={CreateScreen}
      onUpdateResourceItems={handleMoveItems}
      onRemoveResourceItem={handleRemoveItem}
    />
  );
};
